import { FunctionComponent, useState } from "react";
import { Control, Controller, RegisterOptions, UseFormTrigger } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { InputTextarea } from "primereact/inputtextarea";
import { classNames } from "primereact/utils";

import { translationKeys } from "../../../../../models/app-translation-keys";
import { FormField, IFormFieldRules } from "../../FormField/FormField";

import "./TextAreaInputField.scss";

interface ITextAreaInputFormFieldRules extends IFormFieldRules {
  minLength?: number;
  maxLength?: number;
}

interface ITextAreaInputProps {
  control: Control<any, any, any>;
  trigger: UseFormTrigger<any>;
  fieldNameTranslationKey: string;
  fieldDescriptionTranslationKey?: string;
  hideLabel?: boolean;
  fieldName: string;
  rules?: ITextAreaInputFormFieldRules;
}

type TextAreaFormFieldRules = Omit<
  RegisterOptions<any, any>,
  "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
>;

const TextAreaInputField: FunctionComponent<ITextAreaInputProps> = (props: ITextAreaInputProps) => {
  const { t } = useTranslation();

  const [hasCharacterCount, setHasCharacterCount] = useState(props.rules?.maxLength !== undefined);

  const formFieldRules = (): TextAreaFormFieldRules => {
    const rules: TextAreaFormFieldRules = {};
    if (props.rules?.required) {
      rules.required = t(translationKeys.form.error.isRequired, {
        fieldName: t(props.fieldNameTranslationKey),
      });
    }

    if (props.rules?.minLength) {
      rules.minLength = {
        value: props.rules.minLength,
        message: t(translationKeys.form.error.minLength, {
          fieldName: t(props.fieldNameTranslationKey),
          minLength: props.rules.minLength,
        }),
      };
    }

    if (props.rules?.maxLength) {
      rules.maxLength = {
        value: props.rules.maxLength,
        message: t(translationKeys.form.error.maxLength, {
          fieldName: t(props.fieldNameTranslationKey),
          maxLength: props.rules.maxLength,
        }),
      };
    }

    return rules;
  };

  return (
    <Controller
      name={props.fieldName}
      control={props.control}
      rules={formFieldRules()}
      render={({ field, fieldState: { error, invalid } }) => (
        <FormField
          fieldName={props.fieldName}
          fieldNameTranslationKey={props.fieldNameTranslationKey}
          fieldDescriptionTranslationKey={props.fieldDescriptionTranslationKey}
          hideLabel={props.hideLabel}
          error={error}
          invalid={invalid}
        >
          <div
            className={classNames("text-area-input-field", {
              "has-character-count": hasCharacterCount,
            })}
          >
            <InputTextarea
              {...field}
              id={field.name}
              value={field.value ?? ""}
              style={{
                paddingBottom: hasCharacterCount ? "1.6rem" : "inherit",
              }}
              rows={3}
              autoFocus={true}
              className={classNames("", { "p-invalid": invalid })}
            />

            {!!hasCharacterCount && (
              <small className={classNames("character-count", { invalid: invalid })}>
                {!!props.rules?.maxLength && (
                  <span>
                    <span className="value-count">{field.value?.length ?? 0}</span> / {props.rules?.maxLength ?? 100}
                  </span>
                )}

                {!!props.rules?.minLength && !props.rules?.maxLength && <span>{field.value?.length ?? 0}</span>}
              </small>
            )}
          </div>
        </FormField>
      )}
    />
  );
};

export { TextAreaInputField };
